import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { toast } from "sonner";
import {
  Clock, PlayCircle, GraduationCap, ArrowLeft, ChevronRight, ListChecks,
  Award, CheckCircle2, LockKeyhole, Youtube, FileText, Sparkles,
} from "lucide-react";

const LEVEL_LABEL = { iniciante: "Iniciante", intermediario: "Intermediário", avancado: "Avançado" };

export default function CourseDetail() {
  const { slug } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [enrolling, setEnrolling] = useState(false);

  const load = () => {
    setCourse(null); setNotFound(false);
    api.get(`/courses/${slug}`).then(({ data }) => setCourse(data)).catch(() => setNotFound(true));
  };
  useEffect(() => { window.scrollTo(0, 0); load(); }, [slug, user?.id]);

  const enroll = async () => {
    if (!user) { navigate(`/entrar?redirect=/cursos/${slug}`); return; }
    setEnrolling(true);
    try {
      await api.post(`/courses/${slug}/enroll`);
      toast.success("Matrícula confirmada!");
      load();
    } catch (_) { toast.error("Falha ao matricular."); }
    finally { setEnrolling(false); }
  };

  if (notFound) {
    return (
      <div className="container-cuidar py-24 text-center">
        <GraduationCap className="mx-auto h-10 w-10 text-[hsl(var(--muted-foreground))]" />
        <h1 className="mt-4 font-heading text-2xl font-black">Curso não encontrado</h1>
        <Button onClick={() => navigate("/cursos")} className="mt-6 rounded-full">Voltar aos cursos</Button>
      </div>
    );
  }
  if (!course) return <div className="container-cuidar py-14 animate-pulse space-y-3"><div className="h-8 w-72 bg-[hsl(var(--muted))] rounded" /><div className="h-4 w-64 bg-[hsl(var(--muted))] rounded" /><div className="h-96 bg-[hsl(var(--muted))] rounded-2xl mt-8" /></div>;

  const enrolled = !!course.enrollment;
  const progress = course.enrollment?.progress_percent || 0;
  const modulesProgress = course.enrollment?.modules_progress || {};
  const cert = course.certificate;

  return (
    <div>
      <section className="border-b border-[hsl(var(--border))] bg-gradient-to-br from-[hsl(var(--primary))]/8 via-white to-[hsl(var(--secondary))]/8">
        <div className="container-cuidar py-10">
          <button onClick={() => navigate("/cursos")} className="inline-flex items-center gap-1.5 text-sm text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))]" data-testid="course-back-btn">
            <ArrowLeft className="h-4 w-4" /> Voltar aos cursos
          </button>
          <div className="mt-6 grid gap-10 lg:grid-cols-[1.4fr_1fr]">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <Badge className="rounded-full bg-[hsl(var(--primary))]/12 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/12">{course.category}</Badge>
                <Badge variant="outline" className="rounded-full">{LEVEL_LABEL[course.level] || course.level}</Badge>
              </div>
              <h1 className="mt-4 font-heading text-3xl sm:text-4xl font-black leading-tight" data-testid="course-title">{course.title}</h1>
              <p className="mt-4 text-[hsl(var(--muted-foreground))] max-w-2xl">{course.description}</p>
              <div className="mt-6 flex flex-wrap gap-5 text-sm text-[hsl(var(--muted-foreground))]">
                <div className="flex items-center gap-1.5"><PlayCircle className="h-4 w-4" /> {course.modules_count} módulos</div>
                {course.estimated_hours && <div className="flex items-center gap-1.5"><Clock className="h-4 w-4" /> {course.estimated_hours}h</div>}
                <div className="flex items-center gap-1.5 text-[hsl(var(--secondary))]"><Award className="h-4 w-4" /> Certificado (R$ 15)</div>
              </div>
              {enrolled && (
                <div className="mt-6 surface p-5 max-w-lg">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="font-semibold">Seu progresso</span>
                    <span className="text-[hsl(var(--primary))] font-bold">{progress}%</span>
                  </div>
                  <Progress value={progress} className="h-2" />
                  {course.enrollment?.completed_at && (
                    <div className="mt-3 text-xs text-[hsl(var(--primary))] font-semibold flex items-center gap-1">
                      <CheckCircle2 className="h-4 w-4" /> Curso concluído
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="surface p-6 self-start">
              <div className="h-40 rounded-2xl overflow-hidden bg-gradient-to-br from-[hsl(var(--primary))] to-[hsl(var(--primary))]/70 grid place-items-center relative">
                {course.cover_image_url ? (
                  <img src={course.cover_image_url} alt={course.title} className="absolute inset-0 w-full h-full object-cover" />
                ) : (
                  <GraduationCap className="h-20 w-20 text-white/70" />
                )}
              </div>
              {!enrolled ? (
                <>
                  <div className="mt-5 text-center">
                    <div className="text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))]">Acesso ao curso</div>
                    <div className="font-heading text-3xl font-black text-[hsl(var(--primary))] mt-1">Gratuito</div>
                    <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1">Certificado impresso R$ 15,00</div>
                  </div>
                  <Button onClick={enroll} disabled={enrolling} className="mt-5 w-full h-12 rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90" data-testid="course-enroll-btn">
                    {enrolling ? "Matriculando…" : (user ? "Matricular-me agora" : "Entrar para matricular")}
                  </Button>
                </>
              ) : (
                <>
                  <Button onClick={() => {
                    const firstUndone = (course.modules || []).find((m) => !modulesProgress[m.id]?.passed_at);
                    const target = firstUndone || course.modules?.[0];
                    if (target) navigate(`/cursos/${slug}/aula/${target.id}`);
                  }} className="mt-5 w-full h-12 rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90" data-testid="course-continue-btn">
                    Continuar de onde parei <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                  {cert && (
                    <div className="mt-4 rounded-2xl border border-[hsl(var(--secondary))]/40 bg-[hsl(var(--secondary))]/8 p-4 text-center">
                      <Award className="h-6 w-6 mx-auto text-[hsl(var(--secondary))]" />
                      <div className="mt-2 font-heading font-bold text-sm">Certificado disponível</div>
                      <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--muted-foreground))] mt-1">Código {cert.code}</div>
                      <Button onClick={() => navigate("/associado/certificados")} variant="outline" className="mt-3 rounded-full w-full" data-testid="course-view-cert-btn">
                        {cert.paid ? "Baixar certificado" : "Desbloquear (R$ 15)"}
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="container-cuidar py-12">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[hsl(var(--secondary))]">
          <ListChecks className="h-3.5 w-3.5" /> Grade do curso
        </div>
        <h2 className="mt-2 font-heading text-2xl font-black">Módulos e aulas</h2>
        <div className="mt-6 surface p-4 sm:p-6">
          <Accordion type="single" collapsible className="w-full">
            {(course.modules || []).map((m, idx) => {
              const mp = modulesProgress[m.id];
              const passed = !!mp?.passed_at;
              return (
                <AccordionItem key={m.id} value={m.id} data-testid={`module-item-${m.id}`}>
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-4 text-left flex-1">
                      <div className={`h-9 w-9 rounded-full grid place-items-center shrink-0 font-heading font-black text-sm ${
                        passed
                          ? "bg-[hsl(var(--primary))] text-white"
                          : enrolled
                            ? "bg-[hsl(var(--primary))]/12 text-[hsl(var(--primary))]"
                            : "bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]"
                      }`}>
                        {passed ? <CheckCircle2 className="h-4 w-4" /> : (enrolled ? idx + 1 : <LockKeyhole className="h-4 w-4" />)}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-heading font-bold text-base">{m.title}</div>
                        {m.description && <div className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5 line-clamp-1">{m.description}</div>}
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="pl-13 sm:pl-14 space-y-3 text-sm">
                      {m.description && <p className="text-[hsl(var(--muted-foreground))]">{m.description}</p>}
                      <div className="flex flex-wrap gap-2 text-xs">
                        {m.video_url && <Badge variant="outline" className="rounded-full gap-1"><Youtube className="h-3 w-3" /> Vídeo</Badge>}
                        {m.content && <Badge variant="outline" className="rounded-full gap-1"><FileText className="h-3 w-3" /> Aula em texto</Badge>}
                        <Badge variant="outline" className="rounded-full gap-1"><ListChecks className="h-3 w-3" /> Quiz {m.quiz?.questions?.length || 0} perguntas</Badge>
                      </div>
                      {enrolled ? (
                        <Button onClick={() => navigate(`/cursos/${slug}/aula/${m.id}`)}
                          className="mt-2 rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90 h-9"
                          data-testid={`module-open-${m.id}`}>
                          {passed ? "Revisar aula" : "Abrir aula"} <ChevronRight className="h-4 w-4 ml-1" />
                        </Button>
                      ) : (
                        <div className="text-xs text-[hsl(var(--muted-foreground))]">Matricule-se para desbloquear.</div>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </div>

        <div className="mt-10 surface p-6 md:p-8 bg-[hsl(var(--secondary))]/6 flex items-start gap-5">
          <Sparkles className="h-6 w-6 text-[hsl(var(--secondary))] shrink-0 mt-0.5" />
          <div>
            <div className="font-heading font-bold text-lg">Como funciona a certificação</div>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
              Ao concluir todos os módulos com aproveitamento mínimo de 70% no quiz, seu certificado é emitido
              automaticamente. Para baixar a versão em PDF pronta para impressão, é feita uma cobrança única
              de <b>R$ 15,00</b> via Stripe.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
